<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['created']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const form = ref({ name: '', cluster_type_id: '' });

const clusterTypes = computed(() => store.getters['clusterTypes/getTypes']);
const uiFlags = computed(() => store.getters['clusters/getUIFlags']);
const isSaving = computed(() => uiFlags.value.isCreating);
const isNameValid = computed(() => Boolean(form.value.name.trim()));
const isTypeValid = computed(() => Boolean(form.value.cluster_type_id));
const isValid = computed(() => isNameValid.value && isTypeValid.value);

const resetForm = () => {
  form.value = { name: '', cluster_type_id: '' };
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const submit = async () => {
  if (!isValid.value || isSaving.value) return;

  try {
    const created = await store.dispatch('clusters/create', {
      name: form.value.name.trim(),
      cluster_type_id: Number(form.value.cluster_type_id),
    });
    useAlert(t('CLUSTERS.API.CREATE_SUCCESS'));
    emit('created', created);
    close();
  } catch (error) {
    useAlert(t('CLUSTERS.API.ERROR'));
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="lg"
    :title="t('CLUSTERS.CREATE_TITLE')"
    :confirm-button-label="t('CLUSTERS.ACTIONS.CREATE')"
    :disable-confirm-button="!isValid"
    :is-loading="isSaving"
    @confirm="submit"
  >
    <div class="flex flex-col gap-5">
      <!-- Name (hero) -->
      <input
        v-model="form.name"
        type="text"
        :aria-label="t('CLUSTERS.FIELDS.NAME')"
        :placeholder="t('CLUSTERS.NAME_PLACEHOLDER')"
        class="w-full text-lg font-semibold leading-snug bg-transparent rounded-md reset-base text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:bg-n-alpha-1 focus:px-2 focus:-mx-2 focus:py-1"
        @keyup.enter="submit"
      />

      <!-- Cluster type picker -->
      <div class="flex items-center gap-3 min-w-0">
        <span
          class="flex items-center gap-2 text-sm w-28 shrink-0 text-n-slate-11"
        >
          <span class="i-lucide-layers text-n-slate-10" />
          {{ t('CLUSTERS.FIELDS.CLUSTER_TYPE') }}
        </span>
        <select
          v-model="form.cluster_type_id"
          class="flex-1 min-w-0 h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
        >
          <option value="" disabled>{{ t('CLUSTERS.PLACEHOLDERS.CLUSTER_TYPE') }}</option>
          <option
            v-for="ct in clusterTypes"
            :key="ct.id"
            :value="ct.id"
          >
            {{ ct.label_singular }}
          </option>
        </select>
      </div>
    </div>
  </Dialog>
</template>
